import { useContext, useMemo } from "react";
import { VariantContextData } from "../components/variants/VariantBuilderContext";

const INDICATORS_MAPPING = {
  custom_code: {
    type: "custom_code",
    label: "Custom Script",
    color: "#003FFF",
    icon: "fas fa-file-code",
  },
  custom_html: {
    type: "custom_html",
    label: "Custom HTML",
    color: "#003FFF",
    icon: "fas fa-code",
  },
  custom_class: {
    type: "custom_class",
    label: "Custom Class",
    color: "#003FFF",
    icon: "fas fa-tag",
  },
  completion_automation: {
    type: "completion_automation",
    label: "Completion Tracking",
    color: "#003FFF",
    icon: "fas fa-flag-checkered",
  },
  convertflow_automation: {
    type: "convertflow_automation",
    label: "ConvertFlow Automation",
    color: "#003FFF",
    image: "/images/convertflow-icon-new.svg",
  },
  integration_automation: {
    type: "integration_automation",
    label: "Integration Automation",
    color: "#003FFF",
    icon: "fas fa-bolt",
  },
};

const COMPLETION_ACTION_TYPES = [
  "Track completion of CTA",
  "Track completion of campaign",
];

const COLUMN_NAMES = ["first", "second", "third", "fourth"];

const isPresent = (value) =>
  typeof value === "string" && value.trim().length > 0;

const hasScript = (obj) =>
  isPresent(obj?.data?.script) || isPresent(obj?.options?.script);

const hasCustomClass = (obj) =>
  isPresent(obj?.options?.["custom-class"]) ||
  isPresent(obj?.options?.structure?.["custom-class"]);

const hasColumnClass = (obj, column) =>
  isPresent(obj?.options?.structure?.[`cf-column-${column}-custom-class`]);

const hasCustomHtml = (obj) =>
  obj?.element_type == "html" && isPresent(obj.html);

const getAllIndicators = (obj) => {
  const out = [];

  if (hasScript(obj)) {
    out.push(INDICATORS_MAPPING.custom_code);
  }

  if (hasCustomHtml(obj)) {
    out.push(INDICATORS_MAPPING.custom_html);
  }

  if (hasCustomClass(obj)) {
    out.push(INDICATORS_MAPPING.custom_class);
  }

  return out;
};

const getAutomationIndicators = (action) => {
  const actionType = action?.action_type;
  if (!isPresent(actionType)) return EMPTY;

  if (COMPLETION_ACTION_TYPES.includes(actionType)) {
    return [INDICATORS_MAPPING.completion_automation];
  }

  const service = actionType.split(" - ")[0];
  return [
    service !== actionType && service !== "ConvertFlow"
      ? INDICATORS_MAPPING.integration_automation
      : INDICATORS_MAPPING.convertflow_automation,
  ];
};

const getColumnIndicators = (section) => {
  const out = [];
  if (section.layout_type === "flex") return out;

  for (const column of COLUMN_NAMES) {
    if (hasColumnClass(section, column)) {
      out.push({ ...INDICATORS_MAPPING.custom_class, column });
    }
  }
  return out;
};

const EMPTY = Object.freeze([]);

const pushToMap = (map, key, value) => {
  const existing = map.get(key);
  if (existing) {
    existing.push(value);
  } else {
    map.set(key, [value]);
  }
};

const pushStepAggregated = (stepAggregated, stepId, indicator, seenByStep) => {
  if (!stepId) return;
  let seen = seenByStep.get(stepId);
  if (!seen) {
    seen = new Set();
    seenByStep.set(stepId, seen);
  }
  if (seen.has(indicator.label)) return;
  seen.add(indicator.label);
  pushToMap(stepAggregated, stepId, indicator);
};

const buildWalks = (variant) => {
  const steps = variant.steps || {};
  const sections = variant.sections || {};
  const elements = variant.elements || {};
  const fields = variant.fields || {};
  const contentToggleItems = variant.content_toggle_items || {};
  const actionGroups = variant.action_groups || {};

  const gone = (obj) => obj?.toBeDeleted === true;
  const sectionGone = (section) =>
    gone(section) || gone(steps[section?.step_id]);
  const elementGone = (element) =>
    gone(element) || sectionGone(sections[element?.section_id]);
  const elementStepId = (element) =>
    sections[element?.section_id]?.step_id ?? element?.step_id;
  const actionGroupGone = (group) =>
    gone(group) ||
    gone(steps[group?.step_id]) ||
    elementGone(elements[group?.element_id]);
  const actionGroupElementId = (group) =>
    group?.element_id || fields[group?.field_id]?.element_id;
  const actionGroupOf = (action) =>
    action.contactable_type == "ActionGroup"
      ? actionGroups[action.contactable_id]
      : undefined;
  const actionElementId = (action) =>
    action.contactable_type == "ActionGroup"
      ? actionGroupElementId(actionGroupOf(action))
      : action.contactable_type == "Element"
      ? action.contactable_id || action.element_id
      : undefined;
  const sectionParentElement = (section) =>
    elements[
      section.element_id ||
        contentToggleItems[section.content_toggle_item_id]?.element_id
    ];
  const layerAncestorKeys = (element) => {
    const keys = [];
    const visited = new Set();

    while (element && !visited.has(element.id)) {
      visited.add(element.id);
      keys.push(`automations_by_element:${element.id}`);

      const section = sections[element.section_id];
      if (!section) break;

      keys.push(
        section.layout_type === "flex"
          ? `automations_by_section_column:${element.section_column_id}`
          : `automations_by_column:${section.id}-${element.column}`,
        `automations_by_section:${section.id}`
      );
      element = sectionParentElement(section);
    }

    return keys;
  };

  return [
    {
      type: "steps",
      gone,
      stepId: (step) => step.id,
      indicators: getAllIndicators,
    },
    {
      type: "elements",
      gone: elementGone,
      stepId: elementStepId,
      indicators: getAllIndicators,
    },
    {
      type: "action_groups",
      gone: actionGroupGone,
      stepId: (group) =>
        group.step_id || elementStepId(elements[group.element_id]),
      indicators: getAllIndicators,
      extraKeys: (group) => [
        group.step_id && `action_groups_by_step:${group.step_id}`,
        group.element_id && `action_groups_by_element:${group.element_id}`,
      ],
    },
    {
      type: "actions",
      gone: (action) => {
        const group = actionGroupOf(action);
        return (
          gone(action) ||
          (group && actionGroupGone(group)) ||
          elementGone(elements[actionElementId(action)])
        );
      },
      stepId: (action) =>
        actionGroupOf(action)?.step_id ||
        elementStepId(elements[actionElementId(action)]),
      indicators: getAutomationIndicators,
      extraKeys: (action) => {
        const stepId =
          actionGroupOf(action)?.step_id ||
          elementStepId(elements[actionElementId(action)]);
        return [
          ...layerAncestorKeys(elements[actionElementId(action)]),
          stepId && `automations_by_step:${stepId}`,
        ];
      },
    },
    {
      type: "sections",
      gone: sectionGone,
      stepId: (section) => section.step_id,
      indicators: (section) => [
        ...getAllIndicators(section),
        ...getColumnIndicators(section),
      ],
    },
    {
      type: "section_columns",
      gone: (column) =>
        gone(column) || sectionGone(sections[column.section_id]),
      stepId: (column) => sections[column.section_id]?.step_id,
      indicators: getAllIndicators,
    },
  ];
};

const computeIndicators = (variant) => {
  const byId = new Map();
  const stepAggregated = new Map();
  const seenByStep = new Map();
  const buckets = {
    variant: EMPTY,
    steps: [],
    elements: [],
    sections: [],
    section_columns: [],
    action_groups: [],
    actions: [],
  };

  if (!variant) return { byId, buckets, stepAggregated };

  buckets.variant = getAllIndicators(variant);

  for (const walk of buildWalks(variant)) {
    for (const obj of Object.values(variant[walk.type] || {})) {
      if (!obj || walk.gone(obj)) continue;

      const stepId = walk.stepId(obj);
      for (const ind of walk.indicators(obj)) {
        const tagged = { ...ind, id: obj.id };
        buckets[walk.type].push(tagged);
        pushToMap(byId, `${walk.type}:${obj.id}`, tagged);
        for (const key of walk.extraKeys?.(obj) || EMPTY) {
          if (key) pushToMap(byId, key, tagged);
        }
        pushStepAggregated(stepAggregated, stepId, tagged, seenByStep);
      }
    }
  }

  return { byId, buckets, stepAggregated };
};

const contextCache = new WeakMap();

const resolve = (contextData, variant) => {
  const cached = contextCache.get(contextData);
  if (cached) return cached;

  const value = computeIndicators(variant);
  contextCache.set(contextData, value);
  return value;
};

const useCodeIndicators = () => {
  const [contextData] = useContext(VariantContextData);
  const variant = contextData?.objects?.variant;

  const value = resolve(contextData, variant);

  return useMemo(() => {
    const { byId, buckets, stepAggregated } = value;

    const get = (objectType, id, options) => {
      const result = byId.get(`${objectType}:${id}`);
      if (!result) return EMPTY;
      if (!options) return result;

      return result.filter(
        (ind) =>
          (!options.column || ind.column === options.column) &&
          (!options.without_column || !ind.column) &&
          (!options.type || ind.type === options.type)
      );
    };

    const all = () => buckets;

    const forStep = (stepId) => stepAggregated.get(stepId) || EMPTY;

    return { get, all, forStep };
  }, [value]);
};

export default useCodeIndicators;
